import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { cn } from "@/lib/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  TooltipProps,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { format, subMonths, parseISO, isValid, compareAsc, startOfMonth, endOfMonth } from 'date-fns';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { useHeaderData } from "@/hooks/use-header-data";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const Analytics = () => {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { user, organization, organizations } = useHeaderData();

  // Fetch regular expenses
  const { data: expenses, isLoading: expensesLoading } = useQuery({
    queryKey: ["expenses-analytics", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data, error } = await supabase
        .from('expenses')
        .select(`
          *,
          categories (
            name,
            color
          )
        `)
        .eq('organization_id', organizationId)
        .order('date', { ascending: true });
      
      if (error) throw error;
      console.log("Fetched regular expenses for Analytics:", data?.length || 0);
      return data || [];
    },
    enabled: !!organizationId,
  });

  // Fetch expense reports
  const { data: expenseReports, isLoading: reportsLoading } = useQuery({
    queryKey: ["expense-reports-analytics", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data, error } = await supabase
        .from('expense_reports')
        .select(`
          id,
          date_from,
          date_to,
          total_amount,
          currency,
          status
        `)
        .eq('organization_id', organizationId)
        .in('status', ['approved', 'completed'])
        .order('date_from', { ascending: true });
      
      if (error) throw error;
      console.log("Fetched approved expense reports for Analytics:", data?.length || 0);
      return data || [];
    },
    enabled: !!organizationId,
  });

  // Improved monthly trend data calculation with strict date validation and proper aggregation
  const monthlyTrend = React.useMemo(() => {
    if (expensesLoading || reportsLoading) {
      return [];
    }
    
    // Use a Map with a sortable key format (yyyy-MM)
    const monthMap = new Map();
    
    // Process regular expenses
    if (expenses && expenses.length > 0) {
      console.log("Processing expenses for monthly trend:", expenses.length);
      
      expenses.forEach(expense => {
        if (!expense.date) {
          console.warn("Expense missing date:", expense);
          return;
        }
        
        try {
          // Convert various date formats to a Date object, avoiding instanceof
          let expenseDate: Date | null = null;
          
          if (typeof expense.date === 'string') {
            expenseDate = new Date(expense.date);
          } else {
            // Try to convert from timestamp or other formats
            expenseDate = new Date(expense.date as any);
          }
          
          if (!expenseDate || !isValid(expenseDate)) {
            console.warn("Invalid date found:", expense.date);
            return;
          }
          
          // Use yyyy-MM format for both sorting and as a map key
          const monthKey = format(expenseDate, 'yyyy-MM');
          const displayMonth = format(expenseDate, 'MMM yyyy');
          
          if (!monthMap.has(monthKey)) {
            monthMap.set(monthKey, {
              month: displayMonth,
              totalExpenses: 0,
              totalReports: 0,
              total: 0,
              count: 0,
              average: 0,
              sortKey: monthKey  // Keep the sortKey for sorting later
            });
          }
          
          const entry = monthMap.get(monthKey);
          const amount = Number(expense.amount) || 0;
          entry.totalExpenses += amount;
          entry.total += amount;
          entry.count += 1;
          entry.average = entry.total / entry.count;
          
          console.log(`Added expense for ${monthKey}: ${amount} (total now: ${entry.totalExpenses})`);
        } catch (error) {
          console.error("Error processing expense date:", error, expense);
        }
      });
    }
    
    // Process expense reports - same approach as Dashboard
    if (expenseReports && expenseReports.length > 0) {
      console.log("Processing expense reports for monthly trend:", expenseReports.length);
      
      expenseReports.forEach(report => {
        if (!report.date_from) {
          console.warn("Report missing date:", report);
          return;
        }
        
        try {
          // Convert report date to a consistent Date format
          const reportDate = parseISO(report.date_from);
          
          if (!isValid(reportDate)) {
            console.warn("Invalid report date found:", report.date_from);
            return;
          }
          
          // Use yyyy-MM format for both sorting and as a map key
          const monthKey = format(reportDate, 'yyyy-MM');
          const displayMonth = format(reportDate, 'MMM yyyy');
          
          if (!monthMap.has(monthKey)) {
            monthMap.set(monthKey, {
              month: displayMonth,
              totalExpenses: 0,
              totalReports: 0,
              total: 0,
              count: 0,
              average: 0,
              sortKey: monthKey
            });
          }
          
          const entry = monthMap.get(monthKey);
          const amount = Number(report.total_amount) || 0;
          entry.totalReports += amount;
          entry.total += amount;  // Make sure to add report amount to total
          entry.count += 1;
          entry.average = entry.total / entry.count;
          
          // Log the report info for debugging
          console.log(`Added report for ${monthKey}: ${amount} (total now: ${entry.totalReports}, grand total: ${entry.total})`);
        } catch (error) {
          console.error("Error processing report date:", error, report);
        }
      });
    }
    
    // Convert map to array, sort by date
    const result = Array.from(monthMap.values())
      .sort((a, b) => {
        // Sort by the sortKey which is yyyy-MM format
        return a.sortKey.localeCompare(b.sortKey);
      });
      
    // Log the final result for debugging
    console.log("Monthly trend data:", JSON.stringify(result, null, 2));
    
    return result;
  }, [expenses, expenseReports, expensesLoading, reportsLoading]);

  // Category distribution
  const categoryDistribution = React.useMemo(() => {
    if (!expenses || expenses.length === 0) {
      return [];
    }
    
    const categoryMap = new Map();
    
    expenses.forEach(expense => {
      const categoryName = expense.categories?.name || 'Uncategorized';
      const categoryColor = expense.categories?.color || COLORS[0];
      
      if (!categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, {
          name: categoryName,
          value: 0,
          color: categoryColor
        });
      }
      
      categoryMap.get(categoryName).value += Number(expense.amount);
    });
    
    // Convert to array and sort by value (descending)
    return Array.from(categoryMap.values())
      .sort((a, b) => b.value - a.value);
  }, [expenses]);

  // Top expenses
  const topExpenses = React.useMemo(() => {
    if (!expenses || expenses.length === 0) {
      return [];
    }
    
    return [...expenses]
      .sort((a, b) => Number(b.amount) - Number(a.amount))
      .slice(0, 5);
  }, [expenses]);

  // Custom formatter for tooltips to safely handle different value types
  const formatTooltipValue = (value: ValueType): string => {
    if (typeof value === 'number') {
      return `$${value.toFixed(2)}`;
    }
    return `${value}`;
  };

  const isLoading = expensesLoading || reportsLoading;

  if (!user || !organizationId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user}
        organization={organization}
        organizations={organizations}
      />

      <div className="flex min-h-[calc(100vh-4rem)]">
        <Sidebar className="border-r border-border/50" />
        <main className={cn(
          "flex-1 container mx-auto px-4 py-8 space-y-8",
          "relative z-10 bg-background"
        )}>
          <h1 className="text-2xl font-bold mb-6">Expense Analytics</h1>

          {/* Monthly Trend Chart - Updated to include both expense types */}
          <Card className="p-6">
            <CardHeader>
              <CardTitle>Monthly Expense Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                {isLoading ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : monthlyTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart 
                      data={monthlyTrend}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month" 
                        padding={{ left: 30, right: 30 }}
                      />
                      <YAxis 
                        tickFormatter={(value) => `$${value}`}
                      />
                      <Tooltip 
                        formatter={(value: ValueType, name: string) => {
                          // Handle different value types safely
                          const numValue = typeof value === 'number' ? value : 0;
                          return [`$${numValue.toFixed(2)}`, name];
                        }}
                        labelFormatter={(label) => `Month: ${label}`}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="total" 
                        stroke="#8884d8" 
                        name="Total Expenses" 
                        activeDot={{ r: 8 }}
                        strokeWidth={2}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="totalExpenses" 
                        stroke="#82ca9d" 
                        name="Direct Expenses"
                        strokeWidth={2} 
                      />
                      <Line 
                        type="monotone" 
                        dataKey="totalReports" 
                        stroke="#ffc658" 
                        name="Expense Reports"
                        strokeWidth={2} 
                      />
                      <Line 
                        type="monotone" 
                        dataKey="average" 
                        stroke="#ff7300" 
                        name="Average"
                        strokeWidth={1}
                        strokeDasharray="5 5"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full items-center justify-center text-muted-foreground">
                    No expense data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Category Distribution - Updated with improved rendering */}
            <Card className="p-6">
              <CardHeader>
                <CardTitle>Expense by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {categoryDistribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={categoryDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {categoryDistribution.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.color || COLORS[index % COLORS.length]} 
                            />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: ValueType) => {
                          const numValue = typeof value === 'number' ? value : 0;
                          return [`$${numValue.toFixed(2)}`, 'Amount'];
                        }} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground">
                      No category data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Top Expenses - Updated with empty state */}
            <Card className="p-6">
              <CardHeader>
                <CardTitle>Top 5 Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                {topExpenses.length > 0 ? (
                  <div className="space-y-4">
                    {topExpenses.map((expense, index) => (
                      <div key={expense.id} className="flex items-center justify-between border-b pb-2">
                        <div>
                          <span className="font-medium">#{index + 1}</span>
                          <span className="ml-4">{expense.description}</span>
                        </div>
                        <span className="font-bold">${Number(expense.amount).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex h-[200px] items-center justify-center text-muted-foreground">
                    No expense data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Analytics;
